import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, X } from 'lucide-react';
import type { HorarioAula } from '../../types/models';

interface ChamadaForaHorarioModalProps {
  horario: HorarioAula | null;
  onClose: () => void;
  onConfirm: (justificativa?: string) => void;
}

/**
 * Modal de ciência e justificativa pedagógica para chamadas realizadas fora do horário da grade.
 * Permite ao docente prosseguir com o preenchimento, informando que a chamada será auditada pela coordenação.
 */
export function ChamadaForaHorarioModal({
  horario,
  onClose,
  onConfirm,
}: ChamadaForaHorarioModalProps) {
  const [justificativa, setJustificativa] = useState('');

  const handleConfirm = () => {
    onConfirm(justificativa.trim() || undefined);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-card, #ffffff)',
          borderRadius: 20,
          padding: '28px 24px',
          maxWidth: 440,
          width: '100%',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          textAlign: 'left',
          position: 'relative',
          border: '1px solid #fed7aa',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'none',
            border: 'none',
            color: '#94a3b8',
            cursor: 'pointer',
            padding: 4,
            borderRadius: 8,
          }}
          title="Fechar"
        >
          <X size={20} />
        </button>

        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            background: '#fef3c7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
          }}
        >
          <AlertTriangle size={26} color="#d97706" />
        </div>

        <h3
          style={{
            margin: '0 0 4px',
            fontSize: '1.15rem',
            fontWeight: 800,
            color: '#1e293b',
          }}
        >
          Chamada Fora do Horário
        </h3>

        {horario && (
          <div
            style={{
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#d97706',
              marginBottom: 12,
            }}
          >
            {horario.materia?.nome || `${horario.numeroAula}ª Aula`} · Horário oficial: {horario.horaInicio} às {horario.horaFim}
          </div>
        )}

        <div
          style={{
            background: '#fffbeb',
            border: '1px solid #fef3c7',
            borderRadius: 12,
            padding: '12px 14px',
            marginBottom: 16,
            fontSize: '0.82rem',
            color: '#92400e',
            lineHeight: 1.5,
          }}
        >
          O horário oficial desta aula já encerrou. O registro da presença será <strong>permitido normalmente</strong>, mas será encaminhado para <strong>auditoria e aprovação da Coordenação Pedagógica</strong> da escola.
        </div>

        <div style={{ marginBottom: 20 }}>
          <label
            style={{
              display: 'block',
              fontSize: '0.78rem',
              fontWeight: 700,
              color: '#475569',
              marginBottom: 6,
            }}
          >
            Motivo do registro em atraso (opcional):
          </label>
          <textarea
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
            placeholder="Ex: Instabilidade de rede na sala, atividade prática externa, atendimento aos pais..."
            rows={3}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid #cbd5e1',
              fontSize: '0.82rem',
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
            }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              background: '#f1f5f9',
              color: '#475569',
              border: 'none',
              borderRadius: 10,
              padding: '10px 16px',
              fontWeight: 700,
              fontSize: '0.82rem',
              cursor: 'pointer',
            }}
          >
            Voltar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            style={{
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: 10,
              padding: '10px 18px',
              fontWeight: 700,
              fontSize: '0.82rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
            }}
          >
            <CheckCircle2 size={16} />
            Prosseguir com a Chamada
          </button>
        </div>
      </div>
    </div>
  );
}
